import React from 'react'
import Link from 'next/link'
import cn from 'classnames'

import { formatDateDomini } from '@lib/utils'
import { Button } from '@components/Buttons'
import { Box } from '@features/story/components/Box'
import { AffiliationEnum } from '@interfaces/AffiliationEnum'
import { UserAvatar } from '@components/UserAvatar'
import { AdminBadgeIcon } from '@components/icons/AdminBadgeIcon'
import { CheckIcon } from '@components/icons/CheckIcon'
import { WriterInformationProps } from './interface'

export const WriterInformation = ({
  penName,
  publishedAt,
  updatedAt,
  className,
  isFollowedUser,
  isFollowedPenName,
  isToggleFollowUserLoading,
  isToggleFollowPenNameLoading,
  canFollow,
  handleFollowUser,
  handleFollowPenName,
}: WriterInformationProps) => {
  return (
    <Box
      className={cn(
        'card p-[30px] mobile:p-[20px] mobile:pt-0 desktop-tablet:mb-[30px] dark:border-purple',
        className
      )}
    >
      <div className='grid grid-cols-[auto_75px_1fr] mobile:grid-cols-[75px_1fr] items-center gap-x-[60px] gap-y-[20px] mobile:gap-x-[20px] mobile:items-start'>
        <Box.Title className='text-2xl desktop:w-[140px] mobile:col-span-3 dark:text-dark-10'>
          ข้อมูลนักเขียน
        </Box.Title>
        <UserAvatar
          className='min-w-[75px] w-[75px] min-h-[75px] h-[75px]'
          slug={penName.user.profilePageSlug}
          src={penName.user.avatarImgPath}
          alt={penName.user.displayName}
        />
        <div className='flex space-x-[60px] mobile:space-x-0 mobile:flex-col mobile:space-y-[20px]'>
          <div className='mobile:col-span-2 max-w-[260px] flex-1'>
            <Box.Text className='text-secondary-1 line-clamp-1 break-all'>
              {!!penName.user.writer &&
              penName.user.writer.affiliation === AffiliationEnum.PUBLISHER
                ? 'สำนักพิมพ์ : '
                : 'นักเขียน : '}
              <Link
                href={`/profile/${penName.user.profilePageSlug}`}
                prefetch={false}
              >
                <a className='relative font-bold text-secondary dark:text-dark-10 hover:underline cursor-pointer leading-8'>
                  {penName.user.displayName}
                  {(penName.user.isAdmin ||
                    penName.user.email === 'admin@mareads.com') && (
                    <AdminBadgeIcon className='absolute top-0 right-[-30px] w-[21.5px] h-[21px]' />
                  )}
                </a>
              </Link>
            </Box.Text>
            {canFollow && (
              <div className='mt-[10px]'>
                <Button
                  type='button'
                  className={cn('border h-[30px] w-[103px]', {
                    'border-primary': isFollowedUser,
                    'border-secondary-1 dark:border-dark-10': !isFollowedUser,
                  })}
                  onClick={handleFollowUser}
                  disabled={isToggleFollowUserLoading}
                >
                  <Box.Title
                    className={cn('text-sm flex items-center', {
                      'text-primary': isFollowedUser,
                      'text-secondary-1 dark:text-dark-10': !isFollowedUser,
                    })}
                  >
                    {isFollowedUser && (
                      <CheckIcon className='mr-[10px]' width='16' height='16' />
                    )}
                    ติดตาม
                  </Box.Title>
                </Button>
              </div>
            )}
          </div>
          <div className='col-start-3 col-span-1 mobile:col-start-2 mobile:col-span-2 max-w-[260px] flex-1'>
            <Box.Text className='text-secondary-1 line-clamp-1 break-all'>
              นามปากกา :{' '}
              <Link
                href={`/profile/${penName.user.profilePageSlug}?tab=penName&text=${penName.firstPenName}`}
                prefetch={false}
              >
                <a className='font-bold text-secondary dark:text-dark-10 hover:underline cursor-pointer leading-8'>
                  {penName.firstPenName}
                </a>
              </Link>
            </Box.Text>
            {canFollow && (
              <div className='mt-[10px]'>
                <Button
                  type='button'
                  className={cn('border h-[30px] w-[103px]', {
                    'border-primary': isFollowedPenName,
                    'border-secondary-1 dark:border-dark-10':
                      !isFollowedPenName,
                  })}
                  onClick={handleFollowPenName}
                  disabled={isToggleFollowPenNameLoading}
                >
                  <Box.Title
                    className={cn('text-sm flex items-center', {
                      'text-primary': isFollowedPenName,
                      'text-secondary-1 dark:text-dark-10': !isFollowedPenName,
                    })}
                  >
                    {isFollowedPenName && (
                      <CheckIcon className='mr-[10px]' width='16' height='16' />
                    )}
                    ติดตาม
                  </Box.Title>
                </Button>
              </div>
            )}
          </div>
        </div>
      </div>
      <hr className='my-[30px] w-full text-gray-3 dark:border-purple' />
      <div className='flex mobile:flex-col desktop-tablet:items-center mobile:space-y-[10px] space-x-[40px] mobile:space-x-0'>
        <Box.Title className='text-2xl desktop-tablet:w-[160px] dark:text-dark-10'>
          เผยแพร่
        </Box.Title>
        <Box.Text className='text-secondary-1'>
          เผยแพร่เมื่อ :{' '}
          <Box.Name className='dark:text-dark-10'>
            {formatDateDomini(publishedAt)}
          </Box.Name>
        </Box.Text>
        <Box.Text className='text-secondary-1 mobile:pt-[10px]'>
          แก้ไขล่าสุด :{' '}
          <Box.Name className='dark:text-dark-10'>
            {formatDateDomini(updatedAt)}
          </Box.Name>
        </Box.Text>
      </div>
    </Box>
  )
}
